import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useImpersonation } from '@/contexts/ImpersonationContext';

export interface CurrentAppUser {
  id: string;
  auth_user_id: string | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  avatar_url: string | null;
  position: string | null;
  twilio_phone_number: string | null;
  phone: string | null;
  active: boolean;
  is_online: boolean | null;
  is_dnd: boolean | null;
  department_id: string | null;
  system_role_id: string | null;
  manager_id: string | null;
  business_units: string[] | null;
  primary_business_unit: string | null;
  metadata: Record<string, unknown> | null;
  system_role?: {
    id: string;
    name: string;
    code: string;
  } | null;
  department?: {
    id: string;
    name: string;
    code: string;
  } | null;
}

const CURRENT_APP_USER_QUERY_KEY = 'current-app-user';

export function useCurrentAppUser() {
  const { user: authUser } = useAuth();
  const { impersonatedUserId } = useImpersonation();

  return useQuery({
    queryKey: [CURRENT_APP_USER_QUERY_KEY, impersonatedUserId || authUser?.id, !!impersonatedUserId],
    queryFn: async (): Promise<CurrentAppUser | null> => {
      if (impersonatedUserId) {
        // Fetch impersonated user by app_user.id
        const { data, error } = await supabase
          .from('app_users')
          .select(`
            *,
            system_role:system_roles(id, name, code),
            department:departments(id, name, code)
          `)
          .eq('id', impersonatedUserId)
          .maybeSingle();
        
        if (error) {
          console.error('[useCurrentAppUser] Error fetching impersonated user:', error);
          throw error;
        }
        return data as CurrentAppUser | null;
      }

      if (!authUser?.id) return null;
      
      const { data, error } = await supabase
        .from('app_users')
        .select(`
          *,
          system_role:system_roles(id, name, code),
          department:departments(id, name, code)
        `)
        .eq('auth_user_id', authUser.id)
        .maybeSingle();
      
      if (error) {
        console.error('[useCurrentAppUser] Error fetching app user:', error);
        throw error;
      }
      
      return data as CurrentAppUser | null;
    },
    enabled: !!impersonatedUserId || !!authUser?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Get current app user ID from React Query cache synchronously.
 * Useful for mutations that need the user ID without re-fetching.
 */
export function useCurrentAppUserId(): string | null {
  const { data } = useCurrentAppUser();
  return data?.id ?? null;
}

/**
 * Hook to get current app user ID from cache (for use in callbacks/mutations).
 * Returns a function that reads from React Query cache.
 */
export function useGetCurrentAppUserIdFromCache() {
  const queryClient = useQueryClient();
  const { user: authUser } = useAuth();
  
  return (): string | null => {
    const cached = queryClient.getQueryData<CurrentAppUser>([CURRENT_APP_USER_QUERY_KEY, authUser?.id]);
    return cached?.id ?? null;
  };
}
